import { useLayoutEffect, useRef, useState } from 'react';

import { CMD_TONE, TERMINAL } from './commandModel.ts';
import type { LedgerCommand } from './useCommandLedger.ts';
import { duration, utcTime } from '../../components/format.ts';
import type { Tone } from '../../components/state.ts';
import styles from './TransitionTrace.module.css';

/* ==========================================================================
   One command's own timeline, to scale.

   The four-pip LifecycleTrack in a queue row answers "how far did it get" in
   58px. This answers the other half — *when*, and how long each step took —
   by putting the command's real transitions on a real axis: the gaps between
   the nodes are proportional to the gaps between the timestamps, so a command
   that sat pending for four minutes and then failed in two seconds looks
   nothing like one that failed immediately.

   THE HARD PART IS THAT REAL COMMAND TIMES ARE PATHOLOGICALLY CLUSTERED.
   issue → sent → acked happen seconds apart; the command then sits acked for
   hours. On a linear axis spanning that whole life, the first three nodes all
   land inside the same two pixels, and three labels drawn at their true
   fractions overprint into `edssued` / `sentow` and run off the rail's right
   edge. A trace that does that is worse than no trace: it is unreadable AND
   it looks like a rendering bug.

   So the label band is laid out in PIXELS, not percent, and it is laid out
   after measuring:

     1. the rail's own width comes from a ResizeObserver;
     2. each label's width is measured off a canvas using the resolved font of
        two hidden probe spans, so the numbers are the real advance widths of
        the real face rather than a guess;
     3. boxes are clamped into [0, W] — the first label can never start left
        of the rail, the last can never end right of it;
     4. any adjacent pair of boxes that would still touch is MERGED, and the
        merged run renders as one cluster label — `issued→sent 10:37:21Z +17s`
        — which is Foxglove's `(Zoom for more)` idiom: when a run is too dense
        to resolve, say so and give the count, never draw a smear.

   The merge repeats until nothing collides, so the invariant "no two label
   boxes overlap, and no box leaves the rail" holds at every width, for every
   record, with no per-record tuning. Every transition is still listed with
   its full timestamp in the inspector's TRANSITIONS block below — the trace
   is the shape of the time, not the record of it.

   An unsettled command's tail is drawn hatched and open-ended, because the
   right-hand edge is `now` and not an outcome. Nothing is interpolated: every
   node is a recorded transition, and a command whose `issued` event has aged
   out of the audit window draws the two instants its own row carries and says
   so.
   ========================================================================== */

interface Node {
  key: string;
  t: number;
  label: string;
  /** Tone of the pip and the label — the transition itself. */
  tone: Tone;
  /** Tone of the interval that STARTS here: the state the command sat in. */
  seg: Tone;
}

/** A run of nodes whose labels could not be drawn separately at this width. */
interface Cluster {
  key: string;
  /** Every node in the run, in time order. */
  nodes: Node[];
  /** Rail position of the run's midpoint, px. */
  x: number;
  nameLine: string;
  timeLine: string;
  /** Label box, already clamped into the rail. */
  l: number;
  w: number;
  r: number;
  tone: Tone;
  title: string;
}

/** Minimum clear space between two label boxes before they are merged. */
const LABEL_GAP = 8;
/** Slack over the measured text width, so a sub-pixel miss cannot clip a glyph. */
const LABEL_PAD = 5;
/** A gap duration is only printed if its segment is at least this much wider. */
const GAP_PAD = 8;
/** Below this much clear space, `now` is the last node and needs no anchor. */
const NOW_CLEAR = 40;

/* --------------------------------------------------------------------------
   Text measurement. One canvas for the whole app; the font strings are built
   from longhands because the `font` shorthand serialises to "" as soon as any
   related longhand (we set font-variant-numeric globally) is non-initial.
   -------------------------------------------------------------------------- */

let ctx2d: CanvasRenderingContext2D | null | undefined;

function measureText(text: string, font: string): number {
  if (font === '') return text.length * 6;
  if (ctx2d === undefined) ctx2d = document.createElement('canvas').getContext('2d');
  if (ctx2d === null) return text.length * 6;
  ctx2d.font = font;
  return ctx2d.measureText(text).width;
}

function fontOf(el: HTMLElement | null): string {
  if (el === null) return '';
  const cs = getComputedStyle(el);
  return `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
}

interface Metrics {
  /** Rail width in px. 0 until the first measurement lands. */
  w: number;
  nameFont: string;
  timeFont: string;
}

const iso = (t: number) => new Date(t).toISOString();

/* --------------------------------------------------------------------------
   The layout pass.
   -------------------------------------------------------------------------- */

function boxOf(run: Node[], at: (t: number) => number, m: Metrics): Cluster {
  const first = run[0]!;
  const last = run[run.length - 1]!;
  const x = (at(first.t) + at(last.t)) / 2;

  const nameLine =
    run.length === 1
      ? first.label
      : run.length === 2
        ? `${first.label}→${last.label}`
        : `${first.label}→${last.label} ·${run.length}`;

  const spanMs = last.t - first.t;
  const timeLine =
    spanMs > 0 ? `${utcTime(iso(first.t))} +${duration(spanMs)}` : utcTime(iso(first.t));

  const natural =
    Math.max(measureText(nameLine, m.nameFont), measureText(timeLine, m.timeFont)) + LABEL_PAD;
  const w = Math.min(m.w, Math.ceil(natural));

  let l = x - w / 2;
  if (l < 0) l = 0;
  if (l + w > m.w) l = m.w - w;

  return {
    key: run.map((n) => n.key).join('+'),
    nodes: run,
    x,
    nameLine,
    timeLine,
    l,
    w,
    r: l + w,
    tone: last.tone,
    title: run.map((n) => `${n.label} · ${iso(n.t)}`).join('\n'),
  };
}

/**
 * Merge, from the left, any adjacent pair whose boxes would touch — then start
 * again, because merging widens a box and can create a new collision. Ends
 * when a full sweep finds nothing to merge, which is at most `nodes.length`
 * sweeps; the guard is belt and braces.
 */
function layoutClusters(nodes: Node[], at: (t: number) => number, m: Metrics): Cluster[] {
  let runs: Node[][] = nodes.map((n) => [n]);
  for (let guard = 0; guard <= nodes.length + 1; guard += 1) {
    const boxes = runs.map((run) => boxOf(run, at, m));
    let hit = -1;
    for (let i = 0; i < boxes.length - 1; i += 1) {
      if (boxes[i]!.r + LABEL_GAP > boxes[i + 1]!.l) {
        hit = i;
        break;
      }
    }
    if (hit < 0) return boxes;
    runs = [
      ...runs.slice(0, hit),
      [...runs[hit]!, ...runs[hit + 1]!],
      ...runs.slice(hit + 2),
    ];
  }
  return runs.map((run) => boxOf(run, at, m));
}

/* --------------------------------------------------------------------------
   The component.
   -------------------------------------------------------------------------- */

export function TransitionTrace({ cmd, now }: { cmd: LedgerCommand; now: number }) {
  const bandRef = useRef<HTMLDivElement | null>(null);
  const nameProbe = useRef<HTMLSpanElement | null>(null);
  const timeProbe = useRef<HTMLSpanElement | null>(null);
  const [m, setM] = useState<Metrics>({ w: 0, nameFont: '', timeFont: '' });

  useLayoutEffect(() => {
    const band = bandRef.current;
    if (band === null) return;
    const read = () => {
      const w = band.clientWidth;
      const nameFont = fontOf(nameProbe.current);
      const timeFont = fontOf(timeProbe.current);
      setM((p) =>
        p.w === w && p.nameFont === nameFont && p.timeFont === timeFont
          ? p
          : { w, nameFont, timeFont },
      );
    };
    read();
    const ro = new ResizeObserver(read);
    ro.observe(band);
    /* Webfonts land after first paint and change every advance width. */
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(read).catch(() => undefined);
    return () => { ro.disconnect(); };
  }, []);

  const issued = Date.parse(cmd.issued_at);
  /* `issued` is an act, not a state — its pip stays neutral — but the interval
     it opens is the command sitting in `pending`, and that is what the ribbon
     paints. */
  const nodes: Node[] = [
    { key: 'issued', t: issued, label: 'issued', tone: 'neutral', seg: CMD_TONE.pending },
  ];

  if (cmd.events.length > 0) {
    for (const e of cmd.events) {
      if (e.event === 'issued') continue;
      const tone = e.status === null ? 'neutral' : CMD_TONE[e.status];
      nodes.push({ key: `e${e.id}`, t: Date.parse(e.ts), label: e.event, tone, seg: tone });
    }
  } else {
    const updated = Date.parse(cmd.updated_at);
    if (Number.isFinite(updated) && updated > issued) {
      const tone = CMD_TONE[cmd.status];
      nodes.push({ key: 'last', t: updated, label: cmd.status, tone, seg: tone });
    }
  }
  /* The audit log is read newest-first and folded; sort defensively so a
     re-ordered page can never render `cancelled→pending` backwards. */
  nodes.sort((a, b) => a.t - b.t);

  const settled = TERMINAL.has(cmd.status);
  const last = nodes[nodes.length - 1] ?? nodes[0]!;
  const end = settled ? last.t : Math.max(now, last.t + 1);
  const span = Math.max(1, end - issued);
  const f = (t: number) => Math.max(0, Math.min(1, (t - issued) / span));
  const at = (t: number) => f(t) * m.w;

  const total = settled ? last.t - issued : now - issued;

  /* Nothing is positioned until the rail has a width — the bands hold their
     heights meanwhile, so the record does not reflow when it arrives. */
  const ready = m.w >= 48;
  const clusters = ready ? layoutClusters(nodes, at, m) : [];
  const lastBox = clusters[clusters.length - 1];

  /* `now` is an axis end, not a transition. Drop it when the last real node is
     already sitting on the right-hand edge, and whenever it would crowd that
     node's label. */
  const lastX = at(last.t);
  const nowW = ready ? Math.ceil(measureText('now', m.timeFont)) + LABEL_PAD : 0;
  const showNow =
    ready &&
    !settled &&
    m.w - lastX >= NOW_CLEAR &&
    (lastBox === undefined || m.w - nowW > lastBox.r + LABEL_GAP);

  /* Gap durations are printed between clusters, never inside one: an
     intra-cluster gap is by definition narrower than its own label and would
     render as a sliced glyph. The open tail is measured the same way. */
  const gaps: { key: string; l: number; w: number; text: string; open?: true }[] = [];
  if (ready) {
    for (let i = 1; i < clusters.length; i += 1) {
      const a = clusters[i - 1]!.nodes[clusters[i - 1]!.nodes.length - 1]!;
      const b = clusters[i]!.nodes[0]!;
      const l = at(a.t);
      const w = at(b.t) - l;
      const text = duration(b.t - a.t);
      if (w >= measureText(text, m.timeFont) + GAP_PAD) {
        gaps.push({ key: `g${b.key}`, l, w, text });
      }
    }
    if (!settled) {
      const l = lastX;
      const w = m.w - l;
      const text = `+${duration(now - last.t)}`;
      if (w >= measureText(text, m.timeFont) + GAP_PAD) {
        gaps.push({ key: 'gopen', l, w, text, open: true });
      }
    }
  }

  /* The rail is a state ribbon, not a line with dots: every interval between
     two transitions is painted in the tone of the state the command was in
     during it, and the still-running interval is the same tone, hatched. This
     is the highest status encoding the width allows (THE BAR §5) and it is
     what makes a fully collapsed trace still say something — a record that has
     been `acked` for three of its three-and-a-half hours reads as one long
     blue band, at a glance, with no labels at all. */
  const segments: { key: string; l: number; w: number; tone: Tone; open?: true }[] = [];
  if (ready) {
    for (let i = 0; i < nodes.length - 1; i += 1) {
      const a = nodes[i]!;
      const l = at(a.t);
      const w = at(nodes[i + 1]!.t) - l;
      if (w > 0.5) segments.push({ key: `s${a.key}`, l, w, tone: a.seg });
    }
    if (!settled) {
      const l = at(last.t);
      segments.push({ key: 'sopen', l, w: Math.max(0, m.w - l), tone: last.seg, open: true });
    }
  }

  return (
    <div
      className={styles.trace}
      role="img"
      aria-label={
        `${nodes.map((n) => `${n.label} at ${utcTime(iso(n.t))}`).join(', ')}` +
        `${settled ? '' : `, still ${cmd.status} after ${duration(now - last.t)}`}`
      }
    >
      {/* Font probes. Not rendered — measured. */}
      <span ref={nameProbe} className={styles.nameWord} aria-hidden="true" data-probe="true" />
      <span ref={timeProbe} className={styles.nameTime} aria-hidden="true" data-probe="true" />

      <div className={styles.gaps} aria-hidden="true">
        {gaps.map((g) => (
          <span
            key={g.key}
            className={styles.gap}
            data-open={g.open === true ? 'true' : undefined}
            style={{ left: `${g.l}px`, width: `${g.w}px` }}
          >
            {g.text}
          </span>
        ))}
      </div>

      <div className={styles.rail} aria-hidden="true">
        <span className={styles.railBed} />
        {segments.map((s) => (
          <span
            key={s.key}
            className={styles.seg}
            data-tone={s.tone}
            data-open={s.open === true ? 'true' : undefined}
            style={{ left: `${s.l}px`, width: `${s.w}px` }}
          />
        ))}
        {clusters.map((c) => (
          <span
            key={`n${c.key}`}
            className={styles.node}
            data-tone={c.tone}
            data-multi={c.nodes.length > 1 ? 'true' : undefined}
            style={{ left: `${Math.max(0, Math.min(m.w - 7, c.x - 3.5))}px` }}
            title={c.title}
          />
        ))}
      </div>

      <div className={styles.names} ref={bandRef}>
        {clusters.map((c) => (
          <span
            key={`l${c.key}`}
            className={styles.name}
            data-tone={c.tone}
            style={{ left: `${c.l}px`, width: `${c.w}px` }}
            title={c.title}
          >
            <span className={styles.nameWord}>{c.nameLine}</span>
            <span className={styles.nameTime}>{c.timeLine}</span>
          </span>
        ))}
        {showNow && (
          <span className={styles.name} style={{ left: `${m.w - nowW}px`, width: `${nowW}px` }}>
            <span className={styles.nameOpen}>now</span>
          </span>
        )}
      </div>

      <div className={styles.foot}>
        <span className={styles.footKey}>{settled ? 'issue → settle' : 'since issue'}</span>
        <span className={styles.footVal}>{duration(total)}</span>
        {cmd.events.length === 0 && (
          <span className={styles.footNote}>two instants off the row — no audit events in window</span>
        )}
      </div>
    </div>
  );
}
